import { useEffect, useMemo, useState, type CSSProperties } from "react";

export type TagFilterOption = {
  value: string;
  label?: string;
  count?: number;
};

type TagFilterProps = {
  availableTags?: Array<string | TagFilterOption>;
  selectedTags: string[];
  onSelectedTagsChange: (tags: string[]) => void;
  onSearchTags?: (
    query: string,
  ) => Array<string | TagFilterOption> | Promise<Array<string | TagFilterOption>>;
  resultLimit?: number;
  minSearchLength?: number;
};

export function TagFilter({
  availableTags = [],
  selectedTags,
  onSelectedTagsChange,
  onSearchTags,
  resultLimit = 50,
  minSearchLength = 0,
}: TagFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [remoteResults, setRemoteResults] = useState<TagFilterOption[]>([]);
  const [searching, setSearching] = useState(false);

  const normalizedTags = useMemo(
    () => normalizeOptions(availableTags),
    [availableTags],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const localResults = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (normalizedQuery.length < minSearchLength) {
      return normalizedTags.slice(0, resultLimit);
    }

    return normalizedTags
      .filter((tag) => {
        const label = tag.label ?? tag.value;

        return label.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, resultLimit);
  }, [minSearchLength, normalizedTags, query, resultLimit]);

  useEffect(() => {
    if (!open || !onSearchTags) {
      return;
    }

    const normalizedQuery = query.trim();

    if (normalizedQuery.length < minSearchLength) {
      setRemoteResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);

    const timeout = window.setTimeout(() => {
      Promise.resolve(onSearchTags(normalizedQuery))
        .then((results) => {
          if (!cancelled) {
            setRemoteResults(normalizeOptions(results).slice(0, resultLimit));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearching(false);
          }
        });
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [minSearchLength, onSearchTags, open, query, resultLimit]);

  const results = onSearchTags ? remoteResults : localResults;
  const hasSelectedTags = selectedTags.length > 0;
  const canSearch = query.trim().length >= minSearchLength;

  function toggleTag(tag: string) {
    onSelectedTagsChange(
      selectedTags.includes(tag)
        ? selectedTags.filter((selectedTag) => selectedTag !== tag)
        : [...selectedTags, tag],
    );
  }

  return (
    <section style={styles.wrapper} aria-label="Tag filters">
      <div style={styles.summary}>
        <button
          type="button"
          style={styles.openButton}
          onClick={() => setOpen(true)}
        >
          Filter tags
        </button>

        {hasSelectedTags ? (
          <>
            <div style={styles.selectedTags}>
              {selectedTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  style={styles.selectedTag}
                  onClick={() => toggleTag(tag)}
                  aria-label={`Remove ${tag} filter`}
                >
                  {tag} X
                </button>
              ))}
            </div>
            <button
              type="button"
              style={styles.clearButton}
              onClick={() => onSelectedTagsChange([])}
            >
              Clear
            </button>
          </>
        ) : (
          <span style={styles.emptyState}>No tag filters</span>
        )}
      </div>

      {open && (
        <div
          style={styles.overlay}
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            style={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tag-filter-title"
            onClick={(event) => event.stopPropagation()}
          >
            <header style={styles.dialogHeader}>
              <div>
                <h2 id="tag-filter-title" style={styles.dialogTitle}>
                  Filter by tags
                </h2>
                <p style={styles.dialogMeta}>
                  Showing up to {resultLimit} matches
                </p>
              </div>
              <button
                type="button"
                style={styles.iconButton}
                onClick={() => setOpen(false)}
                aria-label="Close tag filter"
              >
                X
              </button>
            </header>

            <input
              autoFocus
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search tags"
              style={styles.searchInput}
            />

            <div style={styles.results} aria-label="Tag results">
              {searching && <div style={styles.resultStatus}>Searching...</div>}

              {!searching && !canSearch && minSearchLength > 0 && (
                <div style={styles.resultStatus}>
                  Type at least {minSearchLength} characters
                </div>
              )}

              {!searching && canSearch && results.length === 0 && (
                <div style={styles.resultStatus}>No tags found</div>
              )}

              {!searching &&
                canSearch &&
                results.map((tag) => {
                  const label = tag.label ?? tag.value;
                  const selected = selectedTags.includes(tag.value);

                  return (
                    <button
                      key={tag.value}
                      type="button"
                      style={{
                        ...styles.resultRow,
                        ...(selected ? styles.resultRowSelected : null),
                      }}
                      onClick={() => toggleTag(tag.value)}
                      aria-pressed={selected}
                    >
                      <span style={styles.resultLabel}>{label}</span>
                      {typeof tag.count === "number" && (
                        <span style={styles.resultCount}>{tag.count}</span>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function normalizeOptions(
  options: Array<string | TagFilterOption>,
): TagFilterOption[] {
  return options.map((option) =>
    typeof option === "string" ? { value: option } : option,
  );
}

const styles = {
  wrapper: {
    marginBottom: "14px",
  },
  summary: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  openButton: {
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text-h)",
    padding: "8px 12px",
    cursor: "pointer",
  },
  clearButton: {
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "8px 12px",
    cursor: "pointer",
  },
  emptyState: {
    color: "var(--text)",
    fontSize: "14px",
  },
  selectedTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  selectedTag: {
    border: "1px solid var(--accent-border)",
    borderRadius: "999px",
    background: "var(--accent-bg)",
    color: "var(--text-h)",
    padding: "5px 10px",
    fontSize: "13px",
    cursor: "pointer",
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "grid",
    placeItems: "center",
    background: "rgba(0, 0, 0, 0.42)",
    padding: "16px",
  },
  dialog: {
    width: "min(560px, 100%)",
    maxHeight: "min(680px, calc(100svh - 32px))",
    display: "flex",
    flexDirection: "column",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    background: "var(--bg)",
    boxShadow: "var(--shadow)",
    overflow: "hidden",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    padding: "16px",
    borderBottom: "1px solid var(--border)",
  },
  dialogTitle: {
    margin: 0,
    fontSize: "20px",
  },
  dialogMeta: {
    marginTop: "4px",
    color: "var(--text)",
    fontSize: "13px",
  },
  iconButton: {
    width: "32px",
    height: "32px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text-h)",
    cursor: "pointer",
  },
  searchInput: {
    margin: "12px 16px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text-h)",
    padding: "10px 12px",
    font: "inherit",
    fontSize: "15px",
  },
  results: {
    minHeight: "220px",
    overflowY: "auto",
    borderTop: "1px solid var(--border)",
  },
  resultStatus: {
    padding: "16px",
    color: "var(--text)",
    fontSize: "14px",
  },
  resultRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    border: 0,
    borderBottom: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text-h)",
    padding: "11px 16px",
    textAlign: "left",
    cursor: "pointer",
  },
  resultRowSelected: {
    background: "var(--accent-bg)",
  },
  resultLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resultCount: {
    color: "var(--text)",
    fontSize: "12px",
  },
} satisfies Record<string, CSSProperties>;
